import React, { useEffect, useState } from "react"
import { Loader } from "@mantine/core"
import { Container, Wrapper } from "QuorumGrassroots/pages/campaign/voter-information/VoterInformationPage.styles"
import { Navigate, useParams } from "react-router-dom"
import { useVoterRegistrationInfo } from "QuorumGrassroots/pages/campaign/voter-information/hooks/useVoterRegistrationInfo"
import { VoterRegistrationSuccess } from "QuorumGrassroots/pages/campaign/voter-information/VoterRegistrationSuccess"
import { CampaignSubpageHeader } from "QuorumGrassroots/widgets/ReusableComponents/CampaignSubpageHeader"
import { useGotvSocialSharing } from "QuorumGrassroots/widgets/GotvCampaignFooter/useGotvSocialSharing"
import { CampaignTitleSubtitle } from "QuorumGrassroots/campaign-forms/components/GotvV2Form/components/CampaignTitleSubtitle/CampaignTitleSubtitle"
import {
    GotvCampaignFooter,
    DEFAULT_SOCIAL_HEADER,
} from "QuorumGrassroots/widgets/GotvCampaignFooter/GotvCampaignFooter"
import { useCampaign } from "QuorumGrassroots/services/grassrootsCampaign"
import { NotRegisteredToVote } from "QuorumGrassroots/pages/campaign/voter-information/components/NotRegisteredToVote/NotRegisteredToVote"

export const VoterInformationPage: React.FC = () => {
    const { id } = useParams<{ id: string }>()
    const { data: campaign, isLoading: isCampaignLoading, isError: isCampaignError } = useCampaign(id)
    const [hasError, setHasError] = useState(false)

    const loggedIn = Boolean(window.userdata && Object.keys(window.userdata || {}).length > 0)

    const { data: voterInfo, error, isLoading } = useVoterRegistrationInfo(id, hasError || !loggedIn)

    const [isRegisteredToVote, setIsRegisteredToVote] = useState<boolean | null>(null)

    // Call hooks unconditionally at the top level before any early returns
    const socialPlatforms = useGotvSocialSharing({
        socialMediaPlatforms: campaign?.social_media_platforms,
        shareUrl: window.location.href,
        shareText: campaign?.social_share_text ?? `Help ${campaign?.name} get out the vote!`,
        imageUrl: campaign?.social_image_url,
    })

    useEffect(() => {
        if (voterInfo) {
            setIsRegisteredToVote(Boolean(voterInfo.verified_voter))
            // eslint-disable-next-line no-console
        } else {
            setIsRegisteredToVote(null)
        }
    }, [voterInfo])

    useEffect(() => {
        if (error) {
            // Remove this line entirely after the functionality is tested
            setIsRegisteredToVote(false)
            setHasError(true)
        }
    }, [error])

    if (!loggedIn) {
        return <Navigate to="/sign_in/" />
    }

    if (isCampaignError) {
        return (
            <Wrapper>
                <Container>
                    <p>Error loading campaign. Please try again later.</p>
                </Container>
            </Wrapper>
        )
    }

    if (isCampaignLoading || !campaign) {
        return (
            <Wrapper>
                <Container>
                    <Loader size="sm" />
                </Container>
            </Wrapper>
        )
    }
    return (
        <Wrapper>
            <Container>
                <CampaignTitleSubtitle campaign={campaign} />
                <CampaignSubpageHeader
                    title="Voter Information"
                    backUrl={`/campaign/${id}`}
                    dataCy="voter-information-header"
                />

                {isLoading ? (
                    <Loader size="sm" />
                ) : (
                    <>
                        {isRegisteredToVote ? (
                            <VoterRegistrationSuccess />
                        ) : (
                            voterInfo && <NotRegisteredToVote voterInfo={voterInfo} />
                        )}
                    </>
                )}

                <GotvCampaignFooter
                    socialPlatforms={socialPlatforms}
                    socialHeading={campaign.social_header ?? DEFAULT_SOCIAL_HEADER}
                />
            </Container>
        </Wrapper>
    )
}
